import { Injectable } from '@angular/core';
import { Howl } from 'howler';

const WINDOW_BACK = 1;
const WINDOW_FORWARD = 2;

export type CacheState = 'loading' | 'ready' | 'error';

export interface CacheEntry {
  howl: Howl;
  state: CacheState;
  generation: number;
}

@Injectable({ providedIn: 'root' })
export class AudioPreloadService {
  private cache = new Map<number, CacheEntry>();
  private cacheGeneration = 0;
  private pendingActiveIndex: number | null = null;

  // ---------------------------------------------------------------------------
  // Window math (static — no instance state needed)
  // ---------------------------------------------------------------------------

  static computeWindow(current: number, total: number): Set<number> {
    if (total === 0) return new Set();
    const window = new Set<number>();
    for (let offset = -WINDOW_BACK; offset <= WINDOW_FORWARD; offset++) {
      window.add(((current + offset) % total + total) % total);
    }
    return window;
  }

  // ---------------------------------------------------------------------------
  // Diff computation
  // ---------------------------------------------------------------------------

  computeDiff(toIndex: number, totalTracks: number): { toEvict: number[]; toCreate: number[] } {
    const newWindow = AudioPreloadService.computeWindow(toIndex, totalTracks);
    const toEvict: number[] = [];
    const toCreate: number[] = [];
    for (const key of this.cache.keys()) {
      if (!newWindow.has(key)) toEvict.push(key);
    }
    for (const key of newWindow) {
      if (!this.cache.has(key)) toCreate.push(key);
    }
    return { toEvict, toCreate };
  }

  // ---------------------------------------------------------------------------
  // Storage: set / get / has / setState / evict
  // ---------------------------------------------------------------------------

  set(index: number, entry: CacheEntry): void {
    this.cache.set(index, entry);
  }

  get(index: number): CacheEntry | undefined {
    return this.cache.get(index);
  }

  has(index: number): boolean {
    return this.cache.has(index);
  }

  setState(index: number, state: CacheState): void {
    const entry = this.cache.get(index);
    if (entry) entry.state = state;
  }

  evict(index: number): void {
    const entry = this.cache.get(index);
    if (!entry) return;
    entry.howl.unload();
    this.cache.delete(index);
  }

  // ---------------------------------------------------------------------------
  // Generation tracking
  // ---------------------------------------------------------------------------

  currentGeneration(): number {
    return this.cacheGeneration;
  }

  isCurrentGeneration(g: number): boolean {
    return g === this.cacheGeneration;
  }

  // ---------------------------------------------------------------------------
  // Pending-active tracking
  // ---------------------------------------------------------------------------

  markPendingActive(index: number | null): void {
    this.pendingActiveIndex = index;
  }

  consumePendingActive(index: number): boolean {
    if (this.pendingActiveIndex !== index) return false;
    this.pendingActiveIndex = null;
    return true;
  }

  // ---------------------------------------------------------------------------
  // Clear
  // ---------------------------------------------------------------------------

  clear(): void {
    for (const entry of this.cache.values()) {
      entry.howl.unload();
    }
    this.cache.clear();
    this.cacheGeneration++;
    this.pendingActiveIndex = null;
  }
}
